import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegacyFALogo from '../ui/LegacyFALogo';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';

/**
 * Shared app layout — wraps all authenticated pages.
 *
 * Props:
 *   children    – page content
 *   fullHeight  – h-screen + overflow-hidden (Signing Space etc.)
 *   activeNav   – 'signing'|'clients'|'templates'|'reports'
 */
const AppLayout = ({ children, fullHeight = false, activeNav = 'signing' }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef   = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/login');
  };

  const NAV_ITEMS = [
    { id: 'signing',   label: 'Signing',   path: '/dashboard' },
    { id: 'clients',   label: 'Clients',   path: null },
    { id: 'templates', label: 'Templates', path: null },
    { id: 'reports',   label: 'Reports',   path: null },
  ];

  const active      = activeNav || (location.pathname.startsWith('/dashboard') ? 'signing' : activeNav);
  const displayName = user?.name || user?.email || 'Advisor';

  return (
    <div className={`${fullHeight ? 'h-screen flex flex-col overflow-hidden' : 'min-h-screen flex flex-col'} bg-canvas`}>

      {/* ═══ Top Bar ═══ */}
      <header className="bg-paper-100 border-b border-ink-100 flex-shrink-0 z-30">
        <div className="h-16 px-6 flex items-center justify-between">

          {/* Left — logo + nav */}
          <div className="flex items-center gap-7">
            <button onClick={() => navigate('/dashboard')} className="flex-shrink-0">
              <LegacyFALogo size="sm" />
            </button>

            <nav className="flex items-center gap-1">
              {NAV_ITEMS.map(item => (
                <button
                  key={item.id}
                  onClick={() => item.path && navigate(item.path)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active === item.id
                      ? 'bg-white text-ink-900 shadow-soft ring-1 ring-ink-100'
                      : 'text-ink-500 hover:text-ink-800'
                  } ${!item.path ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Right — search · bell · divider · avatar */}
          <div className="flex items-center gap-1">
            <button className="p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-white/60 transition-colors">
              <Icon name="search" size={18} />
            </button>
            <button className="p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-white/60 relative transition-colors">
              <Icon name="bell" size={18} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-leaf-500" />
            </button>
            <div className="mx-2 h-6 w-px bg-ink-100" />

            {/* User dropdown trigger */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(v => !v)}
                className={`flex items-center gap-2 pl-1 pr-2.5 py-1.5 rounded-lg transition-colors ${
                  menuOpen ? 'bg-white ring-1 ring-ink-100 shadow-soft' : 'hover:bg-white/60'
                }`}
              >
                <Avatar name={displayName} size={30} />
                <div className="text-left hidden lg:block">
                  <div className="text-xs font-semibold text-ink-800 leading-tight">{displayName}</div>
                  <div className="text-[11px] text-ink-400 leading-tight">{user?.email || ''}</div>
                </div>
                <Icon
                  name="chevronDown"
                  size={14}
                  className={`text-ink-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown */}
              {menuOpen && (
                <div className="absolute right-0 mt-1.5 w-60 bg-white rounded-xl shadow-float ring-1 ring-ink-100 z-50 py-1.5 overflow-hidden">
                  {/* Profile header */}
                  <div className="px-4 py-3 border-b border-ink-100">
                    <div className="flex items-center gap-3">
                      <Avatar name={displayName} size={36} />
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-ink-900 truncate">{displayName}</div>
                        <div className="text-[11px] text-ink-400 truncate">{user?.email || ''}</div>
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <MenuItem icon="user"     label="Profile"       onClick={() => setMenuOpen(false)} />
                    <MenuItem icon="settings" label="Settings"      onClick={() => setMenuOpen(false)} />
                    <MenuItem icon="code"     label="API Docs"      onClick={() => { setMenuOpen(false); window.open('/api-docs', '_blank'); }} />
                    <MenuItem icon="shield"   label="Security"      onClick={() => setMenuOpen(false)} />
                  </div>

                  {/* Divider + Sign out */}
                  <div className="border-t border-ink-100 py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-flame-600 hover:bg-flame-50 transition-colors"
                    >
                      <Icon name="logout" size={15} />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ═══ Page Content ═══ */}
      {children}
    </div>
  );
};

// ── Dropdown menu item ───────────────────────────────────
const MenuItem = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-2 text-[13px] text-ink-700 hover:bg-paper-200 transition-colors"
  >
    <Icon name={icon} size={15} className="text-ink-400" />
    {label}
  </button>
);

export default AppLayout;
